import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Collapse, Form, Modal, Row } from 'react-bootstrap';
import './changes/changesCalendar.css';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { changesApi } from '../services/changesApi';
import type { ChangeCalendarHoliday, ChangeCalendarItem, ChangeMeetingKind, ChangeMeetingLite } from '../types/changes';
import { formatGovernanceDate } from './changes/goLiveWindow';
import { getApiErrorMessage } from '../utils/apiError';
import { isChangeManager } from './changes/changePermissions';
import ChangeScopeFilters, {
  EMPTY_SCOPE_FILTERS,
  hasScopeFilters,
  type ChangeScopeFilterValues,
} from './changes/components/ChangeScopeFilters';
import CalendarSelectedChangeSummary from './changes/components/CalendarSelectedChangeSummary';
import CalendarSelectedMeetingSummary from './changes/components/CalendarSelectedMeetingSummary';
import ChangesCalendarGrid, {
  type CalendarDisplayMode,
} from './changes/components/ChangesCalendarGrid';
import { useActiveUsers } from './changes/hooks/useActiveUsers';
import { useChangeCatalogs } from './changes/hooks/useChangeCatalogs';
import {
  formatDateInput,
  useCalendarWindow,
  type CalendarViewMode,
} from './changes/hooks/useCalendarWindow';

const GRID_ITEM_WARN = 80;

export type CalendarEntityFilter = 'both' | 'changes' | 'meetings';

const ChangesCalendar: React.FC = () => {
  const { user } = useAuth();
  const { showConfirm } = useModal();
  const navigate = useNavigate();
  const canManageAgenda = isChangeManager(user);

  const [viewMode, setViewMode] = useState<CalendarViewMode>('week');
  const [displayMode, setDisplayMode] = useState<CalendarDisplayMode>('executive');
  const [entityFilter, setEntityFilter] = useState<CalendarEntityFilter>('both');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [anchorDate, setAnchorDate] = useState<string>(formatDateInput(new Date()));
  const [filters, setFilters] = useState<ChangeScopeFilterValues>(EMPTY_SCOPE_FILTERS);

  const [items, setItems] = useState<ChangeCalendarItem[]>([]);
  const [meetings, setMeetings] = useState<ChangeMeetingLite[]>([]);
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [meetingKind, setMeetingKind] = useState<ChangeMeetingKind>('camud_go_no_go');
  const [meetingDate, setMeetingDate] = useState(formatDateInput(new Date()));
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingMinutes, setMeetingMinutes] = useState(10);
  const [holidays, setHolidays] = useState<ChangeCalendarHoliday[]>([]);
  const [loading, setLoading] = useState(false);
  const [gridError, setGridError] = useState<string | null>(null);
  const [windowWeekday, setWindowWeekday] = useState(3);
  const [agendaInfo, setAgendaInfo] = useState<string | null>(null);
  const [highlightedChangeId, setHighlightedChangeId] = useState<number | null>(null);
  const [highlightedMeetingId, setHighlightedMeetingId] = useState<number | null>(null);
  const [editingMeetingId, setEditingMeetingId] = useState<number | null>(null);

  const { users } = useActiveUsers({ limit: 40 });
  const { categories } = useChangeCatalogs({ onlyActive: false });
  const { dateFrom, dateTo, days, leadingBlanks } = useCalendarWindow(anchorDate, viewMode);
  const highlightedChange = useMemo(
    () => items.find((item) => item.id === highlightedChangeId) || null,
    [items, highlightedChangeId],
  );
  const highlightedMeeting = useMemo(
    () => meetings.find((item) => item.id === highlightedMeetingId) || null,
    [meetings, highlightedMeetingId],
  );

  const scopeParams = useMemo(
    () => ({
      category_id: filters.categoryId ? Number(filters.categoryId) : undefined,
      project: filters.project.trim() || undefined,
      owner_id: filters.ownerId ? Number(filters.ownerId) : undefined,
    }),
    [filters],
  );

  const visibleItems = useMemo(() => {
    if (entityFilter === 'meetings') return [];
    return items;
  }, [items, entityFilter]);

  const visibleMeetings = useMemo(() => {
    if (entityFilter === 'changes') return [];
    return meetings;
  }, [meetings, entityFilter]);

  useEffect(() => {
    if (entityFilter === 'meetings') setHighlightedChangeId(null);
    if (entityFilter === 'changes') setHighlightedMeetingId(null);
  }, [entityFilter]);

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    setGridError(null);
    try {
      const response = await changesApi.calendar({
        date_from: dateFrom,
        date_to: dateTo,
        ...scopeParams,
      });
      setItems(Array.isArray(response.items) ? response.items : []);
      setMeetings(Array.isArray(response.meetings) ? response.meetings : []);
      setHolidays(Array.isArray(response.holidays) ? response.holidays : []);
      if (typeof response.default_window_weekday === 'number') {
        setWindowWeekday(response.default_window_weekday);
      }
    } catch (err: unknown) {
      setItems([]);
      setMeetings([]);
      setHolidays([]);
      setGridError(getApiErrorMessage(err, 'Falha ao carregar o calendário.'));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, scopeParams]);

  useEffect(() => {
    if (!canManageAgenda) return;
    void loadCalendar();
  }, [canManageAgenda, loadCalendar]);

  if (!canManageAgenda) {
    return <Navigate to="/changes" replace />;
  }

  return (
    <div className="container-fluid py-3">
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h4 className="mb-0">Calendário de Mudanças</h4>
              <div className="small text-muted mt-1">
                {formatGovernanceDate(dateFrom)} → {formatGovernanceDate(dateTo)}
                {loading ? ' · Atualizando…' : null}
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <div className="btn-group" role="group" aria-label="Modo de visualização">
                <Button
                  size="sm"
                  variant={displayMode === 'executive' ? 'primary' : 'outline-primary'}
                  onClick={() => setDisplayMode('executive')}
                >
                  Executiva
                </Button>
                <Button
                  size="sm"
                  variant={displayMode === 'detailed' ? 'primary' : 'outline-primary'}
                  onClick={() => setDisplayMode('detailed')}
                >
                  Detalhada
                </Button>
              </div>
              <div className="btn-group" role="group" aria-label="Período do calendário">
                <Button
                  size="sm"
                  variant={viewMode === 'week' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('week')}
                >
                  Semana
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'month' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('month')}
                >
                  Mês
                </Button>
              </div>
              <div className="btn-group" role="group" aria-label="Conteúdo exibido no calendário">
                <Button
                  size="sm"
                  variant={entityFilter === 'both' ? 'primary' : 'outline-secondary'}
                  onClick={() => setEntityFilter('both')}
                >
                  Tudo
                </Button>
                <Button
                  size="sm"
                  variant={entityFilter === 'meetings' ? 'primary' : 'outline-secondary'}
                  onClick={() => setEntityFilter('meetings')}
                >
                  Reuniões
                </Button>
                <Button
                  size="sm"
                  variant={entityFilter === 'changes' ? 'primary' : 'outline-secondary'}
                  onClick={() => setEntityFilter('changes')}
                >
                  Mudanças
                </Button>
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2 mb-3 small text-muted">
            <span className="me-2">
              <span className="changes-cal-mark-dot riscos" /> Ter · Riscos
            </span>
            <span className="me-2">
              <span className="changes-cal-mark-dot go-live" /> Qui · Go Live / CAMUD
            </span>
            <span className="me-2">
              <span className="changes-cal-mark-dot camud" /> CAMUD
            </span>
            <span className="me-2 text-muted">Reuniões CAB aparecem em azul claro na grade</span>
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-auto text-decoration-none"
              onClick={() => setShowHelp((open) => !open)}
              aria-expanded={showHelp}
            >
              {showHelp ? 'Ocultar ajuda' : 'Como usar'}
            </Button>
          </div>
          <Collapse in={showHelp}>
            <Alert variant="light" className="border mb-3 py-2 small">
              {displayMode === 'executive' ? (
                <>
                  Na visão executiva, clique na linha para abrir o resumo acima (pauta, links,
                  reunião). Dias cheios mostram as primeiras mudanças e o botão “+N neste dia”.
                </>
              ) : (
                <>
                  Clique em uma mudança ou reunião para ver o resumo e ações (pauta, editar,
                  excluir). Excluir reunião não apaga mudanças.
                </>
              )}
            </Alert>
          </Collapse>

          <Row className="g-2 mb-2 align-items-end">
            <Col xs={12} md={4} lg={3}>
              <Form.Label className="small text-muted mb-1">Referência</Form.Label>
              <Form.Control
                type="date"
                aria-label="Data de referência do período"
                title="Data de referência do período"
                value={anchorDate}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    setAnchorDate(value);
                  }
                }}
              />
            </Col>
            <Col xs="auto" className="d-flex gap-2 flex-wrap">
              <Button onClick={() => void loadCalendar()} disabled={loading} size="sm">
                Atualizar
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowAdvanced((open) => !open)}
                aria-expanded={showAdvanced}
              >
                {showAdvanced ? 'Ocultar filtros' : 'Filtros'}
              </Button>
              {canManageAgenda ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setEditingMeetingId(null);
                    setMeetingKind('camud_go_no_go');
                    setMeetingTitle('');
                    setMeetingMinutes(10);
                    setMeetingDate(anchorDate);
                    setCreatingMeeting(true);
                  }}
                >
                  Nova reunião
                </Button>
              ) : null}
            </Col>
          </Row>
          <Collapse in={showAdvanced || displayMode === 'detailed'}>
            <Row className="g-2 mb-3">
              <ChangeScopeFilters
                values={filters}
                onChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
                users={users}
                categories={categories}
                colProps={{ xs: 12, md: 4 }}
              />
              <Col xs={12} md="auto" className="d-flex align-items-end">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setFilters(EMPTY_SCOPE_FILTERS);
                    setAnchorDate(formatDateInput(new Date()));
                  }}
                  disabled={
                    loading || (!hasScopeFilters(filters) && anchorDate === formatDateInput(new Date()))
                  }
                >
                  Limpar filtros
                </Button>
              </Col>
            </Row>
          </Collapse>

          {agendaInfo ? (
            <Alert variant="info" className="py-2" dismissible onClose={() => setAgendaInfo(null)}>
              {agendaInfo}
            </Alert>
          ) : null}
          {gridError ? (
            <Alert variant="danger" className="py-2" dismissible onClose={() => setGridError(null)}>
              {gridError}
            </Alert>
          ) : null}

          {displayMode === 'detailed' && visibleItems.length > GRID_ITEM_WARN ? (
            <Alert variant="warning" className="py-2 small">
              Há muitas mudanças neste período ({visibleItems.length}). Refine a data de referência ou
              os filtros para facilitar a leitura da grade.
            </Alert>
          ) : null}

          {highlightedChange ? (
            <CalendarSelectedChangeSummary
              item={highlightedChange}
              onClear={() => setHighlightedChangeId(null)}
              canSend={canManageAgenda}
              windowStart={`${dateFrom}T00:00:00`}
              windowEnd={`${dateTo}T23:59:59`}
              categoryId={scopeParams.category_id}
              project={scopeParams.project}
              ownerId={scopeParams.owner_id}
              onSent={(message) => setAgendaInfo(message)}
            />
          ) : null}
          {highlightedMeeting ? (
            <CalendarSelectedMeetingSummary
              meeting={highlightedMeeting}
              canManage={canManageAgenda}
              onSent={(message) => setAgendaInfo(message)}
              onClear={() => setHighlightedMeetingId(null)}
              onEdit={() => {
                setEditingMeetingId(highlightedMeeting.id);
                setMeetingKind(highlightedMeeting.kind as ChangeMeetingKind);
                setMeetingTitle(highlightedMeeting.title);
                setMeetingMinutes(Math.max(1, Math.round((highlightedMeeting.countdown_seconds || 600) / 60)));
                setMeetingDate((highlightedMeeting.scheduled_at || '').slice(0, 10) || anchorDate);
                setCreatingMeeting(true);
              }}
              onDelete={() => {
                void (async () => {
                  const ok = await showConfirm({
                    title: 'Excluir reunião',
                    message: `Excluir a reunião "${highlightedMeeting.title}"?`,
                    details:
                      'As mudanças que estavam na pauta não serão apagadas nem alteradas. Some só a reunião.',
                    variant: 'danger',
                    confirmLabel: 'Excluir reunião',
                    cancelLabel: 'Cancelar',
                  });
                  if (!ok) return;
                  try {
                    await changesApi.deleteMeeting(highlightedMeeting.id);
                    setHighlightedMeetingId(null);
                    setAgendaInfo('Reunião excluída. As mudanças da pauta continuam no calendário.');
                    await loadCalendar();
                  } catch (err: unknown) {
                    setGridError(getApiErrorMessage(err, 'Não foi possível excluir a reunião.'));
                  }
                })();
              }}
            />
          ) : null}

          <ChangesCalendarGrid
            days={days}
            leadingBlanks={leadingBlanks}
            items={visibleItems}
            meetings={visibleMeetings}
            holidays={holidays}
            viewMode={viewMode}
            displayMode={displayMode}
            windowWeekday={windowWeekday}
            highlightedId={highlightedChangeId}
            highlightedMeetingId={highlightedMeetingId}
            onCardClick={(item) => {
              setHighlightedMeetingId(null);
              setHighlightedChangeId((current) => (current === item.id ? null : item.id));
            }}
            onMeetingClick={(meeting) => {
              setHighlightedChangeId(null);
              setHighlightedMeetingId((current) => (current === meeting.id ? null : meeting.id));
            }}
          />

          {loading ? <div className="text-muted small mt-2">Carregando grade…</div> : null}
          {!loading && items.length === 0 && meetings.length === 0 ? (
            <Alert variant="light" className="border text-center mt-3 mb-0">
              <div className="fw-semibold">Nenhuma mudança neste período</div>
              <div className="small text-muted mt-1">
                Crie ou edite uma mudança em{' '}
                <Link to="/changes" className="btn btn-sm btn-outline-secondary">
                  Gestão de Mudanças
                </Link>{' '}
                com janela de Go Live, Riscos e Impactos ou CAMUD dentro de{' '}
                {formatGovernanceDate(dateFrom)} → {formatGovernanceDate(dateTo)}.
              </div>
            </Alert>
          ) : null}
        </Card.Body>
      </Card>
      <Modal show={creatingMeeting} onHide={() => setCreatingMeeting(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingMeetingId ? 'Editar reunião CAB' : 'Nova reunião CAB'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Tipo</Form.Label>
            <Form.Select
              value={meetingKind}
              onChange={(e) => setMeetingKind(e.target.value as ChangeMeetingKind)}
            >
              <option value="riscos_impactos">Riscos e Impactos</option>
              <option value="camud_go_no_go">CAMUD Go/No-Go</option>
              <option value="outros">Outros</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Data</Form.Label>
            <Form.Control type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Título (opcional)</Form.Label>
            <Form.Control value={meetingTitle} onChange={(e) => setMeetingTitle(e.target.value)} maxLength={255} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Tempo por mudança (minutos)</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={60}
              value={meetingMinutes}
              onChange={(e) => setMeetingMinutes(Number(e.target.value) || 10)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setCreatingMeeting(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              const payload = {
                kind: meetingKind,
                title: meetingTitle.trim() || undefined,
                scheduled_at: `${meetingDate}T10:00:00`,
                countdown_seconds: Math.min(3600, Math.max(60, meetingMinutes * 60)),
              };
              const request = editingMeetingId
                ? changesApi.updateMeeting(editingMeetingId, payload)
                : changesApi.createMeeting(payload);
              void request
                .then((saved) => {
                  setCreatingMeeting(false);
                  if (editingMeetingId) {
                    setHighlightedMeetingId(saved.id);
                    setAgendaInfo('Reunião atualizada.');
                    void loadCalendar();
                    return;
                  }
                  navigate(`/changes/cab/${saved.id}`);
                })
                .catch((err: unknown) => {
                  setGridError(
                    getApiErrorMessage(
                      err,
                      editingMeetingId ? 'Não foi possível salvar a reunião.' : 'Não foi possível criar a reunião.',
                    ),
                  );
                  setCreatingMeeting(false);
                });
            }}
          >
            {editingMeetingId ? 'Salvar' : 'Criar e abrir CAB'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChangesCalendar;
